import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { getRowId } from 'components/AgGrid/utils';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { AG_GRID_ICON_SETUP } from 'constantsProject';
import type { ChapterEnum, PriceListUerRowData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { CreateOrEditUer } from 'pages/Project/UerProject/Uer/CreateOrEditUer/CreateOrEditUer';
import { HeaderSearchBar, Button, cnMixSpace, IconClose } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { useUerWorkOrderPriceListColumns } from '../../Uer/AddUerToWorkOrder/hooks/useUerWorkOrderPriceListColumns';
import { useUerWorkOrderPriceListFilters } from '../../Uer/AddUerToWorkOrder/hooks/useUerWorkOrderPriceListFilters';

import { useUerWorkOrderPriceListUer } from './hooks/useUerWorkOrderPriceListUer';
import { useUerWorkOrderPriceListUerFilters } from './hooks/useUerWorkOrderPriceListUerFilters';
import { useAddUerFromWorkOrder } from './useAddUerFromWorkOrder';

import Styles from './WorkOrderAddUer.module.css';

interface IWorkOrderAddUERTable {
    onCancel: () => void;
    objectId: number;
    chapter: ChapterEnum;
    reloadWorkOrder: () => void;
    constructionTypeId: number;
    contractId?: number;
    shouldAddToActKC2?: boolean;
    workOrderStatus?: WorkOrderStatusEnum;
}

export const WorkOrderAddUERTable: React.FC<IWorkOrderAddUERTable> = (props) => {
    const {
        onCancel,
        objectId,
        chapter,
        reloadWorkOrder,
        constructionTypeId,
        contractId,
        shouldAddToActKC2 = false,
        workOrderStatus,
    } = props;

    const tableApiRef = useRef<AgGridReact<PriceListUerRowData> | null>(null);

    const { where, onSearchStringClear, onSearchStringChange, searchString, onSearch, changeFilterHandler, filters } =
        useUerWorkOrderPriceListFilters();

    const { workOrderPriceListUerDataSource, allIds } = useUerWorkOrderPriceListUer({
        chapter,
        workOrderObjectId: objectId,
        where,
    });

    const columnsFilters = useUerWorkOrderPriceListUerFilters({
        changeFilterHandler,
        workOrderObjectId: objectId,
        chapter,
    });

    const { columns, uerInputs, checkedIds, setCheckedIds } = useUerWorkOrderPriceListColumns({
        constructionTypeId,
        allIds,
        columnsFilters,
        showNoteInProgress: true,
        workOrderStatus,
    });

    const {
        isSubmitted,
        isLoading,
        addUerData,
        addUerFromWorkOrderStatus,
        addUerFromWorkOrderError,
        handlerAddUerFromWorkOrder,
        handleCloseModalError,
        handleCreateReport,
    } = useAddUerFromWorkOrder(
        shouldAddToActKC2,
        chapter,
        objectId,
        onCancel,
        reloadWorkOrder,
        uerInputs,
        workOrderStatus,
    );

    const isConfirmBtnDisabled = useMemo(() => !Object.keys(checkedIds).length, [checkedIds]);

    const btnConfirmLabel =
        workOrderStatus === WorkOrderStatusEnum.InProgress ? 'Добавить в Акт' : 'Добавить в Наряд-Заказ';

    const refreshCurrentAddTable = useCallback(() => {
        if (tableApiRef.current) {
            tableApiRef.current?.api?.refreshInfiniteCache?.();
        }
    }, []);

    return (
        <ModalDialog
            isOpen
            title="Добавление расценки из прейскуранта расценок"
            onClose={onCancel}
            onConfirm={handlerAddUerFromWorkOrder}
            btnConfirmLabel={btnConfirmLabel}
            withTitleCloseButton
            isCommit={isLoading}
            btnConfirmDisabled={isConfirmBtnDisabled}
            className={Styles.modal}
            leftPanel={
                <CreateOrEditUer
                    constructionTypeId={constructionTypeId}
                    onSave={reloadWorkOrder}
                    contractId={contractId}
                    closeModal={onCancel}
                    chapter={chapter}
                    setCheckedIds={setCheckedIds}
                    resetPageData={refreshCurrentAddTable}
                />
            }
            isOnClickOutsideDisable
        >
            <div className={Styles.modalBody}>
                <div className={cnMixSpace({ mT: 'xl', mB: 'm' })} style={{ position: 'relative' }}>
                    <HeaderSearchBar
                        placeholder="Поиск расценки в прейскуранте"
                        label="поиск"
                        value={searchString}
                        onChange={onSearchStringChange}
                        onSearch={onSearch}
                        style={{ width: '100%', maxWidth: 'none' }}
                    />
                    {!!searchString?.length && (
                        <Button
                            label="Очистить"
                            view="ghost"
                            iconLeft={IconClose}
                            form="round"
                            size="xs"
                            onlyIcon
                            onClick={onSearchStringClear}
                            className="clearSearchStringBtn"
                        />
                    )}
                </div>
                <div className={`${Styles.tableBody} ag-theme-alpine WorkOrderChangePrice`}>
                    <AgGridTable
                        datasource={workOrderPriceListUerDataSource}
                        columnDefs={columns}
                        overlayNoRowsTemplate="Нет данных"
                        getRowId={getRowId}
                        rowModelType="infinite"
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        ref={tableApiRef}
                        icons={AG_GRID_ICON_SETUP}
                        headerHeight={70}
                        suppressDragLeaveHidesColumns
                        tooltipShowDelay={200}
                        key={filters.searchString}
                    />
                </div>
                <ErrorHandlingModal
                    titleText="Добавление расценки из прейскуранта расценок"
                    isSubmitted={isSubmitted}
                    statusRequest={addUerFromWorkOrderStatus}
                    dataRequest={addUerData}
                    errorsRequest={addUerFromWorkOrderError}
                    handleCloseModalError={handleCloseModalError}
                    handleCreateReport={handleCreateReport}
                />
            </div>
        </ModalDialog>
    );
};

WorkOrderAddUERTable.displayName = 'WorkOrderAddUERTable';
